<script>
export default {
  props: {
    prices: { type: Array, default: () => [] },
    priceMode: { type: String, default: 'unified' },
    categoryList: { type: Array, default: () => [] },
    selectedCategoryIds: { type: Array, default: () => [] },
    editMode: { type: Boolean, default: false },
  },

  data() {
    return {
      mode: this.priceMode,
      unifiedPrice: { name: '', cost: 0, origin: 0, price: 0 },
      priceRules: [],
      dialogVisible: false,
      dialogSearch: '',
      dialogSelected: [],
      dialogName: '',
      editingRuleIdx: null,
    }
  },

  computed: {
    isPerCategory: {
      get() {
        return this.mode === 'per_category'
      },
      set(val) {
        this.mode = val ? 'per_category' : 'unified'
        this.$emit('update:priceMode', this.mode)
        this.emitAll()
      },
    },

    // All child categories belonging to currently selected parent categories
    allChildCategories() {
      return this.categoryList.filter((c) => this.selectedCategoryIds.includes(c.id)).flatMap((c) => c.children || [])
    },

    // Child IDs already assigned to a rule (excluding the one being edited)
    usedChildIds() {
      return new Set(this.priceRules.filter((_, i) => i !== this.editingRuleIdx).flatMap((r) => r.categoryIds))
    },

    filteredDialogChildren() {
      const q = this.dialogSearch.trim().toLowerCase()
      return q ? this.allChildCategories.filter((c) => c.title.toLowerCase().includes(q)) : this.allChildCategories
    },

    dialogAllSelectable() {
      return this.filteredDialogChildren.filter((c) => !this.usedChildIds.has(c.id))
    },

    isDialogAllSelected() {
      return this.dialogAllSelectable.length > 0 && this.dialogAllSelectable.every((c) => this.dialogSelected.includes(c.id))
    },

    isDialogIndeterminate() {
      return !this.isDialogAllSelected && this.dialogSelected.some((id) => this.dialogAllSelectable.some((c) => c.id === id))
    },

    // Pre-computed tag labels per rule — avoids re-running getRuleTagLabels on every render
    rulesWithLabels() {
      return this.priceRules.map((rule, idx) => ({
        rule,
        idx,
        tagLabels: this.getRuleTagLabels(rule),
      }))
    },
  },

  watch: {
    // Only re-initialise from prop when the parent (not us) changed it
    prices() {
      if (this._selfEmitting) return
      this.initFromPrices()
    },
    selectedCategoryIds() {
      this.$nextTick(() => {
        // Remove child IDs that no longer belong to any selected parent
        const validIds = new Set(this.allChildCategories.map((c) => c.id))
        this.priceRules = this.priceRules.map((r) => ({ ...r, categoryIds: r.categoryIds.filter((id) => validIds.has(id)) })).filter((r) => r.categoryIds.length > 0)
        this.emitAll()
      })
    },
  },

  created() {
    // Non-reactive flag — avoids Vue making it observable
    this._selfEmitting = false
    this.initFromPrices()
  },

  methods: {
    /* ── Initialisation ─────────────────────────────────── */

    initFromPrices() {
      if (!this.prices?.length) {
        this.unifiedPrice = { name: '', cost: 0, origin: 0, price: 0 }
        this.priceRules = []
        return
      }

      if (this.mode === 'per_category') {
        this.priceRules = this.prices.map((p) => ({
          name: p.name || '',
          categoryIds: Array.isArray(p.categoriesIds) ? p.categoriesIds : Array.isArray(p.categoryIds) ? p.categoryIds : p.categoryId != null ? [p.categoryId] : [],
          cost: Number(p.cost) || 0,
          origin: Number(p.origin) || 0,
          price: Number(p.price) || 0,
        }))
      } else {
        const p = this.prices[0] || {}
        this.unifiedPrice = {
          name: p.name || '',
          cost: Number(p.cost) || 0,
          origin: Number(p.origin) || 0,
          price: Number(p.price) || 0,
        }
      }
    },

    /* ── Emit ───────────────────────────────────────────── */

    emitAll() {
      const result =
        this.mode === 'per_category'
          ? this.priceRules.map((rule) => ({
              name: rule.name,
              categoriesIds: [...rule.categoryIds],
              cost: rule.cost,
              origin: rule.origin,
              price: rule.price,
            }))
          : [{ ...this.unifiedPrice }]

      this._selfEmitting = true
      this.$emit('update:prices', result)
      this.$nextTick(() => {
        this._selfEmitting = false
      })
    },

    /* ── Dialog ─────────────────────────────────────────── */

    openAddDialog() {
      this.editingRuleIdx = null
      this.dialogSearch = ''
      this.dialogSelected = []
      this.dialogName = ''
      this.dialogVisible = true
    },

    openEditDialog(idx) {
      this.editingRuleIdx = idx
      this.dialogSearch = ''
      this.dialogSelected = [...this.priceRules[idx].categoryIds]
      this.dialogName = this.priceRules[idx].name || ''
      this.dialogVisible = true
    },

    toggleDialogAll() {
      if (this.isDialogAllSelected) {
        const selectableIds = new Set(this.dialogAllSelectable.map((c) => c.id))
        this.dialogSelected = this.dialogSelected.filter((id) => !selectableIds.has(id))
      } else {
        const existing = new Set(this.dialogSelected)
        this.dialogAllSelectable.forEach((c) => existing.add(c.id))
        this.dialogSelected = Array.from(existing)
      }
    },

    toggleCat(id) {
      const idx = this.dialogSelected.indexOf(id)
      if (idx >= 0) this.dialogSelected.splice(idx, 1)
      else this.dialogSelected.push(id)
    },

    confirmDialog() {
      if (this.editingRuleIdx !== null) {
        this.$set(this.priceRules, this.editingRuleIdx, {
          ...this.priceRules[this.editingRuleIdx],
          name: this.dialogName,
          categoryIds: [...this.dialogSelected],
        })
      } else {
        this.priceRules.push({ name: this.dialogName, categoryIds: [...this.dialogSelected], cost: 0, origin: 0, price: 0 })
      }
      this.emitAll()
      this.dialogVisible = false
    },

    /* ── Helpers ────────────────────────────────────────── */

    removeRule(idx) {
      this.priceRules.splice(idx, 1)
      this.emitAll()
    },

    // Returns ['父分類|子分類', ...] for a rule's categoryIds
    getRuleTagLabels(rule) {
      const tags = []
      rule.categoryIds.forEach((id) => {
        const parent = this.categoryList.find((p) => this.selectedCategoryIds.includes(p.id) && (p.children || []).some((c) => c.id === id))
        if (!parent) return
        const child = (parent.children || []).find((c) => c.id === id)
        if (child) tags.push(`${parent.title}|${child.title}`)
      })
      return tags
    },

    validate() {
      if (this.mode === 'per_category' && this.priceRules.length === 0) {
        this.$message.warning('請至少新增一條價格規則')
        return Promise.reject(new Error('no rules'))
      }
      return Promise.resolve()
    },
  },
}
</script>

<template>
  <div>
    <!-- ── Header: toggle + add button ───────────────────── -->
    <div :class="$style.headerBar">
      <div :class="$style.modeToggle">
        <span :class="isPerCategory ? $style.inactiveLabel : $style.activeLabel">統一價格</span>
        <el-switch v-model="isPerCategory" style="margin: 0 10px;" />
        <span :class="isPerCategory ? $style.activeLabel : $style.inactiveLabel">價格規則</span>
      </div>
      <el-button v-if="isPerCategory" round type="primary" icon="el-icon-plus" @click="openAddDialog">
        新增價格規則
      </el-button>
    </div>

    <!-- ── Price table ────────────────────────────────────── -->
    <div :class="$style.priceTable">
      <div :class="$style.tableHead">
        <div :class="$style.colItem">項目</div>
        <div :class="$style.colPrice">成本</div>
        <div :class="$style.colPrice">原價</div>
        <div :class="$style.colPrice">售價</div>
        <div v-if="isPerCategory" :class="$style.colActions" />
      </div>

      <!-- Unified: single row -->
      <template v-if="!isPerCategory">
        <div :class="$style.tableRow">
          <div :class="$style.colItem">
            <el-input v-model="unifiedPrice.name" placeholder="請輸入名稱" size="small" @change="emitAll" />
          </div>
          <div :class="$style.colPrice">
            <el-input-number v-model="unifiedPrice.cost" :min="0" controls-position="right" style="width:100%;" @change="emitAll" />
          </div>
          <div :class="$style.colPrice">
            <el-input-number v-model="unifiedPrice.origin" :min="0" controls-position="right" style="width:100%;" @change="emitAll" />
          </div>
          <div :class="$style.colPrice">
            <el-input-number v-model="unifiedPrice.price" :min="0" controls-position="right" style="width:100%;" @change="emitAll" />
          </div>
        </div>
      </template>

      <!-- Per-category: one row per rule, zebra-striped -->
      <template v-else>
        <div v-for="{ rule, idx, tagLabels } in rulesWithLabels" :key="idx" :class="idx % 2 === 0 ? $style.tableRow : $style.tableRowAlt">
          <div :class="$style.ruleContent">
            <div :class="$style.rowTop">
              <div :class="$style.colItem">
                <el-input v-model="rule.name" placeholder="請輸入名稱" size="small" @change="emitAll" />
              </div>
              <div :class="$style.colPrice">
                <el-input-number v-model="rule.cost" :min="0" controls-position="right" style="width:100%;" @change="emitAll" />
              </div>
              <div :class="$style.colPrice">
                <el-input-number v-model="rule.origin" :min="0" controls-position="right" style="width:100%;" @change="emitAll" />
              </div>
              <div :class="$style.colPrice">
                <el-input-number v-model="rule.price" :min="0" controls-position="right" style="width:100%;" @change="emitAll" />
              </div>
              <div :class="$style.colActions">
                <el-button type="text" @click="openEditDialog(idx)">修改</el-button>
                <el-button type="text" :class="$style.deleteBtn" @click="removeRule(idx)">刪除</el-button>
              </div>
            </div>
            <div v-if="tagLabels.length" :class="$style.categoryTagsRow">
              <span :class="$style.categoryLabel">適用類別：</span>
              <el-tag v-for="tag in tagLabels" :key="tag" size="mini" type="info" :class="$style.categoryTag">{{ tag }}</el-tag>
            </div>
          </div>
        </div>
      </template>
    </div>

    <!-- ── Category selection dialog ─────────────────────── -->
    <el-dialog title="選擇寵物分類" :visible.sync="dialogVisible" width="620px" top="8vh" append-to-body>
      <div :class="$style.dialogBody">
        <el-input v-model="dialogSearch" placeholder="搜尋分類" prefix-icon="el-icon-search" :class="$style.dialogSearch" />

        <div :class="$style.selectAllRow" @click="toggleDialogAll">
          <span :class="[$style.catCheckbox, isDialogAllSelected && $style.catCheckboxChecked, isDialogIndeterminate && $style.catCheckboxIndeterminate]" />
          <span>全選</span>
        </div>

        <div :class="$style.catGrid">
          <div
            v-for="cat in filteredDialogChildren"
            :key="cat.id"
            :class="[$style.catTag, dialogSelected.includes(cat.id) && $style.catTagSelected, usedChildIds.has(cat.id) && $style.catTagDisabled]"
            @click="!usedChildIds.has(cat.id) && toggleCat(cat.id)"
          >
            <span :class="[$style.catCheckbox, dialogSelected.includes(cat.id) && $style.catCheckboxChecked]" />
            <span :class="$style.catTitle">{{ cat.title }}</span>
          </div>
        </div>
      </div>

      <div slot="footer" :class="$style.dialogFooter">
        <el-button :class="$style.dialogBtn" round @click="dialogVisible = false">取消</el-button>
        <el-button :class="$style.dialogBtn" round type="primary" :disabled="!dialogSelected.length" @click="confirmDialog">確定</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<style lang="scss" module>
$orange: #ea6a2a;
$orange-light: rgba(234, 106, 42, 0.08);
$border: #ebeef5;
$head-bg: #f5f7fa;
$text-muted: #909399;
$text-main: #303133;

@mixin table-row-base {
  display: flex;
  align-items: center;
  padding: 14px 16px;
  border-top: 1px solid $border;
  min-height: 64px;
  gap: 8px;
  transition: background 0.15s;
}

/* ── Header bar ────────────────────────── */
.headerBar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 14px;
}

.modeToggle {
  display: flex;
  align-items: center;
  font-size: 14px;
  font-weight: 500;
}

.activeLabel {
  color: $orange;
  font-weight: 600;
  transition: color 0.2s;
}

.inactiveLabel {
  color: $text-muted;
  transition: color 0.2s;
}

/* ── Price table ───────────────────────── */
.priceTable {
  border: 1px solid $border;
  border-radius: 6px;
  overflow: hidden;
  background: #fff;
}

.tableHead {
  display: flex;
  align-items: center;
  background: $head-bg;
  padding: 10px 16px;
  font-size: 13px;
  color: $text-muted;
  font-weight: 500;
  gap: 8px;
}

.tableRow {
  @include table-row-base;
  background: #fff;
  &:hover {
    background: rgba(234, 106, 42, 0.04);
  }
}

.tableRowAlt {
  @include table-row-base;
  background: $head-bg;
  &:hover {
    background: rgba(234, 106, 42, 0.06);
  }
}

.colItem {
  flex: 1;
  min-width: 0;
  font-size: 14px;
  color: $text-main;
  line-height: 1.6;
  padding-right: 8px;
}

.colPrice {
  width: 150px;
  flex-shrink: 0;
  text-align: center;

  :global {
    .el-input-number .el-input__inner {
      text-align: center;
    }
  }
}

.colActions {
  width: 88px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: flex-end;
}

.ruleContent {
  display: flex;
  flex-direction: column;
  width: 100%;
  gap: 8px;
}

.rowTop {
  display: flex;
  align-items: center;
  gap: 8px;
}

.categoryTagsRow {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 4px;
}

.categoryLabel {
  font-size: 12px;
  color: $text-muted;
  white-space: nowrap;
  flex-shrink: 0;
}

.categoryTag {
  margin-right: 2px;
}

.deleteBtn {
  color: #f56c6c !important;
}

/* ── Dialog ────────────────────────────── */
.dialogBody {
  padding: 0 4px 4px;
}
.dialogSearch {
  margin-bottom: 14px;
}

.selectAllRow {
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  user-select: none;
  font-size: 14px;
  color: $text-main;
  padding-bottom: 12px;
  margin-bottom: 12px;
  border-bottom: 1px solid $border;
  &:hover {
    color: $orange;
  }
}

/* ── Category tag grid ─────────────────── */
.catGrid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
  max-height: 400px;
  overflow-y: auto;
  padding-right: 2px;
}

.catTag {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  border: 1px solid #dcdfe6;
  border-radius: 6px;
  padding: 12px 14px;
  cursor: pointer;
  user-select: none;
  font-size: 14px;
  color: $text-main;
  min-height: 50px;
  transition: border-color 0.18s, background-color 0.18s, color 0.18s;

  &:hover:not(.catTagDisabled) {
    border-color: $orange;
    color: $orange;
  }
}

.catTagSelected {
  border-color: $orange;
  background-color: $orange-light;
  color: $orange;
}

.catTagDisabled {
  opacity: 0.45;
  cursor: not-allowed;
}

.catTitle {
  flex: 1;
  line-height: 1.5;
  word-break: break-all;
}

/* ── Custom checkbox indicator ─────────── */
.catCheckbox {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  min-width: 16px;
  border: 1px solid #dcdfe6;
  border-radius: 3px;
  background: #fff;
  margin-top: 1px;
  flex-shrink: 0;
  position: relative;
  transition: background 0.18s, border-color 0.18s;

  &::after {
    content: '';
    display: none;
    width: 4px;
    height: 8px;
    border: 1.5px solid #fff;
    border-top: none;
    border-left: none;
    transform: rotate(45deg) translate(-1px, -1px);
  }
}

.catCheckboxChecked {
  background: $orange;
  border-color: $orange;
  &::after {
    display: block;
  }
}

.catCheckboxIndeterminate {
  background: $orange;
  border-color: $orange;
  &::after {
    display: block;
    width: 8px;
    height: 0;
    border-bottom: 2px solid #fff;
    border-right: none;
    transform: none;
    margin: 0;
  }
}

/* ── Dialog footer ─────────────────────── */
.dialogFooter {
  display: flex;
  gap: 12px;
  padding: 0 4px;
}

.dialogBtn {
  flex: 1;
}
</style>
